/**
 * Local cart model (ТЗ §8-10) — a simple, in-memory selection of products + quantities.
 *
 * The cart lives only in the client; it carries *intent* (which products, how many) and
 * never prices anything. The server re-prices the selection (``POST /api/cart/quote/``) for
 * the bottom panel and freezes prices when the order is created (``POST /api/orders/`` with
 * ``items``). This module is pure (no React, no I/O) so the reducer is trivially testable.
 */
import type { ProductListItem } from "@entities/product";

/** One selected product and how many of it the user wants. */
export interface CartLine {
  readonly product: ProductListItem;
  readonly qty: number;
}

/** The cart, keyed by product id (insertion order is preserved for stable rendering). */
export type CartItems = ReadonlyMap<number, CartLine>;

/** Upper bound per line — a guardrail; the server is the real authority on limits. */
export const MAX_QTY = 99;

export type CartAction =
  | { readonly type: "add"; readonly product: ProductListItem }
  | { readonly type: "increment"; readonly productId: number }
  | { readonly type: "decrement"; readonly productId: number }
  | { readonly type: "remove"; readonly productId: number }
  | { readonly type: "clear" };

/** The empty cart. */
export const EMPTY_CART: CartItems = new Map();

/** Reduce a cart action into the next immutable cart. */
export function cartReducer(state: CartItems, action: CartAction): CartItems {
  switch (action.type) {
    case "add": {
      const next = new Map(state);
      const existing = next.get(action.product.id);
      const qty = Math.min((existing?.qty ?? 0) + 1, MAX_QTY);
      next.set(action.product.id, { product: action.product, qty });
      return next;
    }
    case "increment": {
      const existing = state.get(action.productId);
      if (existing === undefined) {
        return state;
      }
      const qty = Math.min(existing.qty + 1, MAX_QTY);
      if (qty === existing.qty) {
        return state;
      }
      const next = new Map(state);
      next.set(action.productId, { ...existing, qty });
      return next;
    }
    case "decrement": {
      const existing = state.get(action.productId);
      if (existing === undefined) {
        return state;
      }
      const next = new Map(state);
      if (existing.qty <= 1) {
        next.delete(action.productId);
      } else {
        next.set(action.productId, { ...existing, qty: existing.qty - 1 });
      }
      return next;
    }
    case "remove": {
      if (!state.has(action.productId)) {
        return state;
      }
      const next = new Map(state);
      next.delete(action.productId);
      return next;
    }
    case "clear":
      return state.size === 0 ? state : new Map();
  }
}

/** Total number of units across all lines (the "N товаров" figure). */
export function cartCount(state: CartItems): number {
  let total = 0;
  for (const line of state.values()) {
    total += line.qty;
  }
  return total;
}

/** Number of distinct products selected. */
export function cartLineCount(state: CartItems): number {
  return state.size;
}

/** Quantity selected for a given product (0 when not in the cart). */
export function lineQty(state: CartItems, productId: number): number {
  return state.get(productId)?.qty ?? 0;
}

/** Lines as an array, in insertion order. */
export function cartLines(state: CartItems): readonly CartLine[] {
  return [...state.values()];
}

/** The untrusted ``items`` payload for the quote / order endpoints. */
export function toItemsPayload(
  state: CartItems,
): readonly { readonly product_id: number; readonly qty: number }[] {
  return cartLines(state).map((line) => ({
    product_id: line.product.id,
    qty: line.qty,
  }));
}

/** Stable signature of the current selection (drives the idempotency key on checkout). */
export function cartSignature(state: CartItems): string {
  return cartLines(state)
    .map((line) => `${line.product.id}x${line.qty}`)
    .join(",");
}
